import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import s from './HeroSection.module.css';

const DESKTOP = [
  '/hero-images/zohar-25.webp',
  '/hero-images/zohar-24.webp',
  '/hero-images/zohar-23.webp',
  '/hero-images/sivan-22.webp',
  '/hero-images/zohar-17.webp',
];
const MOBILE = [
  '/hero-images/zohar-kitchen.webp',
  '/hero-images/gal-13.webp',
  '/hero-images/gila-dining.webp',
  '/hero-images/hila-pool.webp',
  '/hero-images/gal-living.webp',
];

const INTERVAL = 4500;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const transitioning = useRef(false);
  const imagesRef = useRef([]);
  const zoomRef = useRef(null);
  const timerRef = useRef(null);
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  const images = isMobile ? MOBILE : DESKTOP;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const startZoom = useCallback((idx) => {
    zoomRef.current?.kill();
    const el = imagesRef.current[idx];
    if (!el) return;
    gsap.set(el, { scale: 1.12 });
    zoomRef.current = gsap.to(el, { scale: 1, duration: 6, ease: 'none' });
  }, []);

  const transitionTo = useCallback((nextIdx) => {
    if (transitioning.current) return;
    transitioning.current = true;
    const curEl = imagesRef.current[current];
    const nxtEl = imagesRef.current[nextIdx];
    if (!curEl || !nxtEl) { transitioning.current = false; return; }
    const tl = gsap.timeline({
      onComplete: () => { setCurrent(nextIdx); transitioning.current = false; }
    });
    gsap.set(nxtEl, {
      clipPath: isMobile ? 'inset(100% 0 0 0)' : 'inset(0 100% 0 0)',
      opacity: 1, scale: 1.12, zIndex: 2,
    });
    startZoom(nextIdx);
    tl.to(nxtEl, { clipPath: 'inset(0 0% 0 0)', duration: 1.2, ease: 'power3.inOut' }, 0);
    tl.set(curEl, { opacity: 0, scale: 1, zIndex: 1 });
    tl.set(nxtEl, { zIndex: 1 });
  }, [current, isMobile, startZoom]);

  useEffect(() => {
    const firstEl = imagesRef.current[0];
    if (firstEl) { gsap.set(firstEl, { opacity: 1, scale: 1.12, zIndex: 1 }); startZoom(0); }
  }, [startZoom, isMobile]);

  useEffect(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => transitionTo((current + 1) % images.length), INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [current, images.length, transitionTo]);

  useEffect(() => {
    if (!heroRef.current || !contentRef.current) return;
    const tl = gsap.timeline();
    tl.fromTo(heroRef.current, { opacity: 0 }, { opacity: 1, duration: 1.6, ease: 'power2.out' });
    tl.to(contentRef.current, { opacity: 1, duration: 0 }, 1);
    const els = contentRef.current.querySelectorAll('[data-a]');
    els.forEach((el, i) => {
      tl.fromTo(el, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out' }, 1.1 + i * 0.1);
    });
    return () => tl.kill();
  }, []);

  const goTo = (idx) => {
    if (idx === current) return;
    clearInterval(timerRef.current);
    transitionTo(idx);
    timerRef.current = setInterval(() => transitionTo((idx + 1) % images.length), INTERVAL);
  };

  return (
    <section className={s.hero} ref={heroRef}>
      <div className={s.slides}>
        {images.map((src, i) => (
          <img key={src} ref={el => imagesRef.current[i] = el}
            src={src} alt="" className={s.slide}
            style={{ opacity: i === 0 ? 1 : 0 }} />
        ))}
      </div>

      <div className={s.overlay} />
      <div className={s.vignette} />

      <div className={s.content} ref={contentRef}>
        {/* Top  - Masthead */}
        <div className={s.top}>
          <div data-a className={s.mastheadWrap}>
            <span className={s.mastheadLine} />
            <h2 className={s.masthead}>LANGER</h2>
            <span className={s.mastheadLine} />
          </div>
          <div data-a className={s.mastheadSub}>Architecture & Design Studio</div>
        </div>

        {/* Center */}
        <div className={s.center}>
          <h1 data-a className={s.headline}>
            כל חלום מתחיל בחזון
            <br />
            <span className={s.headlineAccent}>אנחנו הופכים אותו לבית</span>
          </h1>
          <p data-a className={s.tagline}>
            סטודיו לאדריכלות ועיצוב פנים שלוקח את החזון שלכם
            <br />
            ומתרגם אותו לבית שנראה כמו מהמגזינים  - וחי בדיוק כמוכם
          </p>
          <div data-a className={s.ctas}>
            <button className={s.btn}>
              <span className={s.btnLabel}>צפו בפרויקטים</span>
            </button>
            <button className={s.btn}>
              <span className={s.btnLabel}>לשיחת היכרות</span>
            </button>
          </div>
        </div>

        {/* Bottom */}
        <div className={s.bottom}>
          <div data-a className={s.counter}>
            <span className={s.counterCur}>{String(current + 1).padStart(2, '0')}</span>
            <span className={s.counterBar} />
            <span className={s.counterTotal}>{String(images.length).padStart(2, '0')}</span>
          </div>

          <div data-a className={s.pips}>
            {images.map((_, i) => (
              <button key={i} className={`${s.pip} ${i === current ? s.pipOn : ''}`}
                onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`}>
                <div className={s.pipFill} />
              </button>
            ))}
          </div>

          <div data-a className={s.scrollCol}>
            <span className={s.scrollTxt}>SCROLL</span>
            <div className={s.scrollRail}><div className={s.scrollBead} /></div>
          </div>
        </div>
      </div>
    </section>
  );
}
